import React, { useState, useEffect } from "react";
import { NavLink, Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import NotificationBell from "./NotificationBell";
import ForcePasswordChange from "./ForcePasswordChange";
import ForcePrivacyConsent from "./ForcePrivacyConsent";
import {
  LayoutDashboard, Users, CalendarCheck, HeartHandshake,
  Send, LogOut, Flame, Menu, X, User, Users2, HandHeart, FileText, CalendarDays, HandCoins, MessagesSquare, Image, Mic, ShieldCheck, GraduationCap, PhoneCall, BookOpen, ScrollText, MailPlus, UserPlus, History, ChevronDown, Search, LifeBuoy, Phone, Mail, Clock3, QrCode, KeyRound, BellRing, Eye, MapPin, AlertTriangle, Inbox,
} from "lucide-react";

const isStaffRole = (u) => ["admin", "pastor", "leader"].includes(u?.role);

// Personal ("member") menu — the congregant experience, available to everyone.
function personalNav(user) {
  if (user?.role === "member") {
    return [
      { to: "/portal", label: "My Portal", icon: User, testid: "nav-portal" },
      { to: "/my-classes", label: "My Courses", icon: GraduationCap, testid: "nav-my-classes" },
      { to: "/my-teams", label: "My Teams", icon: Users2, testid: "nav-my-teams" },
      { to: "/my-cell-group", label: "My Cell Group", icon: MapPin, testid: "nav-my-cell-group" },
      { to: "/shared-qr-codes", label: "Church QR Codes", icon: QrCode, testid: "nav-shared-qr" },
      { to: "/calendar", label: "Calendar", icon: CalendarDays, testid: "nav-calendar" },
      { to: "/gallery", label: "Gallery", icon: Image, testid: "nav-gallery" },
      { to: "/sermons", label: "Sermons", icon: Mic, testid: "nav-sermons" },
      ...(user?.giving_open ? [{ to: "/giving", label: "Giving", icon: HandCoins, testid: "nav-giving" }] : []),
    ];
  }
  // Staff & team leaders: personal congregant pages (church-management items live under Church Administration)
  return [
    { to: "/portal", label: "My Portal", icon: User, testid: "nav-portal" },
    { to: "/my-classes", label: "My Courses", icon: GraduationCap, testid: "nav-my-classes" },
    { to: "/my-teams", label: "My Teams", icon: Users2, testid: "nav-my-teams" },
    { to: "/my-cell-group", label: "My Cell Group", icon: MapPin, testid: "nav-my-cell-group" },
  ];
}

// Church Administration — grouped submenus. Each item shows only if the user has access.
const ADMIN_GROUPS = [
  { key: "dashboard", label: "Church Dashboard", icon: LayoutDashboard, items: [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard", show: (u) => u.caps?.view_dashboard },
  ]},
  { key: "membership", label: "Membership Management", icon: Users, items: [
    { to: "/members", label: "Members", icon: Users, testid: "nav-members", show: (u) => u.caps?.manage_members },
    { to: "/pastoral-care", label: "Pastoral Care", icon: HeartHandshake, testid: "nav-pastoral-care", show: (u) => u.caps?.pastoral_care },
    { to: "/follow-up", label: "Follow-up", icon: PhoneCall, testid: "nav-follow-up", show: (u) => u.caps?.pastoral_care },
    { to: "/reply-inbox", label: "Reply Inbox", icon: Inbox, testid: "nav-reply-inbox", show: (u) => u.caps?.pastoral_care },
    { to: "/evangelism", label: "Evangelism", icon: UserPlus, testid: "nav-evangelism", show: (u) => u.caps?.evangelism || u.caps?.evangelism_follow_up || u.caps?.manage_members },
    { to: "/visitor-care", label: "Visitor Care", icon: HandHeart, testid: "nav-visitor-care", show: (u) => u.caps?.visitor_care || u.caps?.manage_members },
    { to: "/prayer-requests", label: "Prayer Requests", icon: HeartHandshake, testid: "nav-prayer-requests", show: (u) => u.caps?.view_prayers },
  ]},
  { key: "teams", label: "Teams", icon: Users2, direct: true, items: [
    { to: "/teams", label: "Teams", icon: Users2, testid: "nav-teams", show: (u) => u.caps?.manage_teams },
  ]},
  { key: "courses", label: "Course Management", icon: GraduationCap, items: [
    { to: "/classes", label: "Courses", icon: GraduationCap, testid: "nav-classes", show: (u) => u.caps?.manage_content },
    { to: "/feedback-board", label: "Learner Replies", icon: MessagesSquare, testid: "nav-feedback-board", show: (u) => u.caps?.manage_content },
  ]},
  { key: "cellgroup", label: "Cell Group", icon: Users2, items: [
    { to: "/cell/overview", label: "Overview", icon: LayoutDashboard, testid: "nav-cell-overview", show: (u) => u.caps?.cell_view },
    { to: "/cell/responses", label: "Responses", icon: ScrollText, testid: "nav-cell-responses", show: (u) => u.caps?.cell_view },
    { to: "/cell/postcode-groups", label: "Postcode groups", icon: MapPin, testid: "nav-cell-postcodes", show: (u) => u.caps?.cell_view },
    { to: "/cell/planning", label: "Group planning", icon: Users2, testid: "nav-cell-planning", show: (u) => u.caps?.cell_view },
    { to: "/cell/data-quality", label: "Data quality", icon: AlertTriangle, testid: "nav-cell-quality", show: (u) => u.caps?.cell_view },
    { to: "/cell/audit", label: "Cell Audit", icon: History, testid: "nav-cell-audit", show: (u) => u.caps?.cell_view },
  ]},
  { key: "general", label: "General Church Management", icon: CalendarDays, items: [
    { to: "/attendance", label: "Attendance", icon: CalendarCheck, testid: "nav-attendance", show: (u) => u.caps?.view_attendance },
    { to: "/conversions", label: "Conversions", icon: HeartHandshake, testid: "nav-conversions", show: (u) => u.caps?.view_conversions },
    { to: "/calendar", label: "Calendar", icon: CalendarDays, testid: "nav-calendar", show: (u) => u.caps?.view_calendar },
    { to: "/documents", label: "Documents", icon: FileText, testid: "nav-documents", show: (u) => u.caps?.view_documents },
    { to: "/gallery", label: "Gallery", icon: Image, testid: "nav-gallery", show: (u) => u.caps?.view_gallery },
    { to: "/sermons", label: "Sermons", icon: Mic, testid: "nav-sermons", show: (u) => u.caps?.view_sermons },
    { to: "/giving", label: "Giving", icon: HandCoins, testid: "nav-giving", show: (u) => u.role === "admin" || !!u.giving_open },
    { to: "/messaging", label: "Messaging", icon: Send, testid: "nav-messaging", show: (u) => u.caps?.send_messaging },
    { to: "/church-qr-codes", label: "Church QR Codes", icon: QrCode, testid: "nav-church-qr", show: (u) => u.role !== "member" },
  ]},
  { key: "system", label: "Systems Admin", icon: ShieldCheck, items: [
    { to: "/users", label: "Users & Access", icon: ShieldCheck, testid: "nav-users", show: (u) => u.caps?.is_admin },
    { to: "/roles-permissions", label: "Roles & Permissions", icon: KeyRound, testid: "nav-roles-permissions", show: (u) => u.caps?.is_admin },
    { to: "/alerts", label: "Alerts", icon: BellRing, testid: "nav-alerts", show: (u) => u.caps?.is_admin },
    { to: "/calling-audit", label: "Calling Audit", icon: PhoneCall, testid: "nav-calling-audit", show: (u) => u.caps?.is_admin },
    { to: "/access-overview", label: "Access Overview", icon: Eye, testid: "nav-access-overview", show: (u) => u.caps?.is_admin },
    { to: "/message-log", label: "Message Log", icon: ScrollText, testid: "nav-message-log", show: (u) => u.caps?.view_message_log },
    { to: "/deletion-log", label: "Deletion Log", icon: History, testid: "nav-deletion-log", show: (u) => u.caps?.is_admin },
    { to: "/portal-settings", label: "Portal Settings", icon: LifeBuoy, testid: "nav-portal-settings", show: (u) => u.caps?.is_admin },
    { to: "/message-templates", label: "Message Templates", icon: MailPlus, testid: "nav-message-templates", show: (u) => u.caps?.is_admin || u.caps?.is_pastor },
  ]},
];

function adminGroups(user) {
  if (!user) return [];
  const personalRoutes = new Set(personalNav(user).map((i) => i.to));
  return ADMIN_GROUPS
    .map((g) => ({ ...g, items: g.items.filter((i) => i.show(user) && !personalRoutes.has(i.to)) }))
    .filter((g) => g.items.length > 0);
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const personal = personalNav(user);
  const groups = adminGroups(user);
  const groupsKey = `sj_nav_groups_${user?.id || "anon"}`;
  const [openGroups, setOpenGroups] = useState(() => {
    let stored = {};
    try { stored = JSON.parse(localStorage.getItem(groupsKey) || "{}") || {}; } catch { stored = {}; }
    const init = {};
    groups.forEach((g) => { init[g.key] = g.key in stored ? !!stored[g.key] : g.items.some((i) => location.pathname.startsWith(i.to)); });
    return init;
  });
  const [q, setQ] = useState("");
  const [support, setSupport] = useState(null);
  const [pendingTestimonies, setPendingTestimonies] = useState(0);
  useEffect(() => {
    if (!user?.caps?.view_prayers) return;
    const fetchCount = () => api.get("/testimonies/pending/count").then(({ data }) => setPendingTestimonies(data.count || 0)).catch(() => {});
    fetchCount();
    const id = setInterval(fetchCount, 60000);
    return () => clearInterval(id);
  }, [user, location.pathname]);
  useEffect(() => {
    api.get("/support-contact")
      .then(({ data }) => { if (data && (data.phone || data.email || data.hours)) setSupport(data); })
      .catch(() => {});
  }, []);
  const toggleGroup = (key) => setOpenGroups((o) => ({ ...o, [key]: !o[key] }));

  // Remember each person's expanded/collapsed choices between visits.
  useEffect(() => {
    try { localStorage.setItem(groupsKey, JSON.stringify(openGroups)); } catch { /* ignore */ }
  }, [openGroups, groupsKey]);

  // Auto-open the group containing the active route when navigating.
  useEffect(() => {
    const active = groups.find((g) => g.items.some((i) => location.pathname.startsWith(i.to)));
    if (active) setOpenGroups((o) => (o[active.key] ? o : { ...o, [active.key]: true }));
  }, [location.pathname]); // eslint-disable-line

  const guideItem = { to: "/guide", label: "User Guide", icon: BookOpen, testid: "nav-guide" };
  const allItems = [...personal, ...groups.flatMap((g) => g.items), guideItem];
  const query = q.trim().toLowerCase();
  const searchResults = query ? allItems.filter((i) => i.label.toLowerCase().includes(query)) : [];

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  const navItemClass = ({ isActive }) =>
    `flex items-center gap-3 px-4 py-3 mx-3 rounded-xl transition-colors font-medium text-[15px] ${
      isActive ? "shadow-[0_6px_18px_-8px_rgba(0,0,0,0.45)]" : "hover:bg-white/10"
    }`;
  const navItemStyle = ({ isActive }) => ({
    background: isActive ? "#fff" : "transparent",
    color: isActive ? "var(--sj-primary)" : "rgba(255,255,255,0.86)",
  });

  const renderItem = (n, opts = {}) => {
    const badge = n.to === "/prayer-requests" ? pendingTestimonies : 0;
    return (
    <NavLink
      key={n.to}
      to={n.to}
      end={n.to === "/dashboard" || n.to === "/portal"}
      data-testid={n.testid}
      onClick={() => { setOpen(false); setQ(""); }}
      className={({ isActive }) => `${navItemClass({ isActive })} ${opts.nested ? "!mx-3 !pl-9 !py-2.5 text-[14px]" : ""}`}
      style={navItemStyle}
    >
      <n.icon className={opts.nested ? "h-4 w-4" : "h-5 w-5"} style={opts.accent ? { color: "var(--sj-accent)" } : undefined} />
      <span className={opts.accent ? "font-semibold text-[15px]" : ""}>{n.label}</span>
      {badge > 0 && (
        <span data-testid="nav-testimony-badge" className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[11px] font-bold" style={{ background: "var(--sj-accent)", color: "#063B30" }} title={`${badge} testimony(ies) awaiting review`}>{badge}</span>
      )}
    </NavLink>
    );
  };

  const SidebarInner = (
    <div className="flex flex-col h-full w-full text-white" style={{ background: "var(--sj-sidebar)" }}>
      <div className="flex items-center gap-3 px-5 h-20" data-testid="sidebar-logo">
        <div className="h-11 w-11 rounded-xl flex items-center justify-center font-head font-extrabold text-sm shrink-0" style={{ background: "var(--sj-accent)", color: "#063B30" }}>SOJ</div>
        <div className="leading-tight min-w-0">
          <div className="font-head font-bold text-[17px] truncate">Streams of Joy</div>
          <div className="text-[11px] uppercase tracking-[0.18em] text-white/60">Erith London</div>
        </div>
      </div>
      <div className="px-4 pt-4">
        <div className="relative">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/60" />
          <input
            data-testid="nav-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search menu…"
            className="w-full pl-9 pr-8 py-2.5 rounded-xl text-sm outline-none bg-white/10 border border-white/15 text-white placeholder:text-white/50 focus:bg-white/15 focus:ring-2 focus:ring-[var(--sj-accent)]/50"
          />
          {q && (
            <button type="button" data-testid="nav-search-clear" onClick={() => setQ("")} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-white/60">
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
      <nav className="flex-1 py-4 space-y-1 overflow-y-auto">
        {query ? (
          <div data-testid="nav-search-results">
            {searchResults.length === 0 ? (
              <p className="px-6 py-4 text-sm text-white/60">No pages match “{q}”.</p>
            ) : (
              searchResults.map((n) => renderItem(n))
            )}
          </div>
        ) : (
          <>
            {personal.map((n) => renderItem(n))}

            {groups.length > 0 && (
              <div className="pt-4" data-testid="church-admin-section">
                <div className="px-6 pb-1 flex items-center justify-between gap-2">
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-white/50">Church Administration</span>
                  <button
                    type="button"
                    data-testid="nav-collapse-all"
                    onClick={() => { const anyOpen = groups.some((g) => !g.direct && openGroups[g.key]); setOpenGroups(Object.fromEntries(groups.filter((g) => !g.direct).map((g) => [g.key, !anyOpen]))); }}
                    className="text-[11px] font-medium hover:underline"
                    style={{ color: "var(--sj-accent)" }}
                  >
                    {groups.some((g) => !g.direct && openGroups[g.key]) ? "Collapse all" : "Expand all"}
                  </button>
                </div>
                {groups.map((g) => g.direct ? (
                  <div key={g.key} className="mb-0.5">{renderItem(g.items[0], { accent: true })}</div>
                ) : (
                  <div key={g.key} className="mb-0.5">
                    <button
                      type="button"
                      data-testid={`nav-group-${g.key}`}
                      aria-expanded={!!openGroups[g.key]}
                      onClick={() => toggleGroup(g.key)}
                      className="w-full flex items-center gap-3 px-4 py-3 mx-3 rounded-xl transition-colors font-semibold text-[15px] hover:bg-white/10"
                      style={{ color: "#fff", width: "calc(100% - 1.5rem)" }}
                    >
                      <g.icon className="h-5 w-5" style={{ color: "var(--sj-accent)" }} />
                      <span className="flex-1 text-left">{g.label}</span>
                      <ChevronDown className={`h-4 w-4 transition-transform ${openGroups[g.key] ? "rotate-180" : ""}`} />
                    </button>
                    {openGroups[g.key] && <div className="mt-0.5 space-y-0.5">{g.items.map((n) => renderItem(n, { nested: true }))}</div>}
                  </div>
                ))}
              </div>
            )}

            <div className="pt-3">
              {renderItem(guideItem)}
            </div>
          </>
        )}
      </nav>
      <div className="p-3">
        <div className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/5 px-3 py-3">
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold truncate text-white">{user?.name}</div>
            <div className="text-xs capitalize" style={{ color: "var(--sj-accent)" }}>{(user?.role || "").replace("_", " ")}</div>
          </div>
          <button
            data-testid="logout-button"
            onClick={doLogout}
            title="Sign out"
            aria-label="Sign out"
            className="h-10 w-10 shrink-0 flex items-center justify-center rounded-xl border border-white/20 text-white transition-colors hover:bg-white/10"
          >
            <LogOut className="h-4 w-4" />
          </button>
        </div>
        <Link to="/privacy" data-testid="sidebar-privacy-link" className="mt-2 block text-center text-[11px] hover:underline text-white/55">
          Privacy &amp; Data Protection Policy
        </Link>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex" style={{ background: "var(--sj-bg)" }}>
      <aside className="hidden md:flex w-64 shrink-0 sticky top-0 h-screen">
        {SidebarInner}
      </aside>

      {open && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <div className="absolute left-0 top-0 h-full w-64">{SidebarInner}</div>
        </div>
      )}

      <div className="flex-1 min-w-0 flex flex-col">
        <header className="h-16 flex items-center justify-between px-4 md:px-8 border-b sticky top-0 z-40 backdrop-blur-md" style={{ borderColor: "var(--sj-border)", background: "rgba(241,244,242,0.85)" }}>
          <div className="flex items-center gap-3">
            <button data-testid="mobile-menu-button" onClick={() => setOpen(true)} className="md:hidden"><Menu className="h-6 w-6" /></button>
            <span className="md:hidden font-head font-bold">Streams of Joy</span>
          </div>
          <div className="flex items-center gap-1">
            <NotificationBell />
          </div>
        </header>
        <main className="flex-1 p-4 sm:p-6 md:p-8">{children}</main>
        {support && (
          <footer data-testid="portal-support-footer" className="px-6 md:px-8 py-4 border-t text-sm flex flex-wrap items-center gap-x-6 gap-y-1.5" style={{ borderColor: "var(--sj-border)", background: "#fff", color: "var(--sj-text-2)" }}>
            <span className="inline-flex items-center gap-1.5 font-semibold" style={{ color: "var(--sj-primary)" }}><LifeBuoy className="h-4 w-4" /> Members Portal Support</span>
            {support.phone && <a href={`tel:${support.phone}`} data-testid="support-footer-phone" className="inline-flex items-center gap-1.5 hover:underline"><Phone className="h-4 w-4" />{support.phone}</a>}
            {support.email && <a href={`mailto:${support.email}`} data-testid="support-footer-email" className="inline-flex items-center gap-1.5 hover:underline"><Mail className="h-4 w-4" />{support.email}</a>}
            {support.hours && <span data-testid="support-footer-hours" className="inline-flex items-center gap-1.5"><Clock3 className="h-4 w-4" />{support.hours}</span>}
          </footer>
        )}
      </div>
      <ForcePasswordChange />
      <ForcePrivacyConsent />
    </div>
  );
}
